import { useState } from "react";
import { Send, UserPlus, CircleCheck, CircleX, Trash } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useInvitations } from "@/hooks/useInvitations";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import LoadingIndicator from "./LoadingIndicator";
import { useQueryClient } from "@tanstack/react-query";

const Team = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isInvitationLoading, setIsInvitationLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [memberToDelete, setMemberToDelete] = useState<{
    userId: string | null,
    userEmail: string | null,
    invitationId: string | null,
    invitationEmail: string | null,
  } | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    invitations,
    isInvitationsLoading,
    sendInvitationEmail,
    deleteInvitation,
    deleteTeamMember,
  } = useInvitations()

  const queryClient = useQueryClient();

  const invitationSchema = z.object({
    email: z.string().email("Please enter a valid email")
  })

  type invitationFormType = z.infer<typeof invitationSchema>;

  const { 
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<invitationFormType>({
    resolver: zodResolver(invitationSchema),
    defaultValues: { email: "" }
  });

  const onSubmit = async (data: invitationFormType) => {
    setIsInvitationLoading(true)
    await sendInvitationEmail({ email: data.email }, {
      onSuccess: (response) => {
        reset();

        toast({
          title: "Invitations Sent",
          variant: "default",
          icon: <CircleCheck size={30} className="text-green-500 animate-spin-once"/>,
          description: response.data.message,
          duration: 3000,
        })
        queryClient.invalidateQueries({ queryKey: ["invitations"] });
        setIsInvitationLoading(false)
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          variant: "destructive",
          icon: <CircleX size={30} className="text-red-500"/>,
          description: error?.response?.data?.message ?? "Oops, something went wrong.",
          duration: 3000,
        })
        setIsInvitationLoading(false)
      }
    })
  }

  const handleDeleteClick = ({
    userId,
    userEmail,
    invitationId,
    invitationEmail,
  } : {
    userId: string | null,
    userEmail: string | null,
    invitationId: string | null,
    invitationEmail: string | null,
  }) => {
    setMemberToDelete({
      userId,
      userEmail,
      invitationId,
      invitationEmail,
    });
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (memberToDelete) {
      setShowDeleteDialog(false);

      if (memberToDelete.userId) {
        await deleteTeamMember({
          memberId: memberToDelete.userId,
          deletionReason: `Deleted by ${user?.userId}`
        }, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["invitations"] })
            toast({
              title: "Member Removed",
              description: `${memberToDelete.userEmail} has been removed from the team.`,
              variant: "default",
              duration: 5000,
            });
          }
        })
      } else if (memberToDelete.invitationId) {
        // logic to cancel invitation
        await deleteInvitation(memberToDelete.invitationId, {
          onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["invitations"] });

            toast({
              title: "Invitation cancelled",
              description: `Invitation of ${response.data.email} has been cancelled.`,
              variant: "default",
              duration: 5000,
            });
          }
        })
      } else {
        console.error("User id or invitation id is not found!!!")
        toast({
          title: "Something went wrong",
          description: `Unable to delete member or invitaion`,
          variant: "destructive",
          duration: 5000,
          icon: <CircleX size={30} />
        });
      }
    }
  };

  
  if (!invitations || isInvitationsLoading) {
    return <LoadingIndicator />
  }

  const filteredMembers = invitations.filter((member) => {
    const userEmailMatch = member?.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;
    const invitationEmailMatch = member?.invitationEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;
    const isValid = member?.status !== "Invalidated";

    return isValid && (userEmailMatch || invitationEmailMatch);
  });

  return (
    <div className="min-h-screen flex items-start justify-center p-4">
      <div className="w-full max-w-4xl space-y-4">
        {/* Invite Form */}
        <Card className="w-full shadow">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <UserPlus className="h-6 w-6 text-blue-500" />
              <CardTitle>Invite Team Members</CardTitle>
            </div>
            <CardDescription>
              Add team members to collaborate on your workspace
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent>
              <div className="space-y-4">
                <div className="flex-row items-center space-x-2">
                  <Input
                    placeholder="colleague@company.com"
                    className="flex-1"
                    {...register("email")}
                  />
                  { errors.email && 
                    <p className='text-red-500 text-sm'>
                      {errors.email.message}
                    </p> 
                  }
                </div>
              </div>
            </CardContent>

            <CardFooter>
              <Button type="submit" className="w-full" disabled={isInvitationLoading}>
                <Send className="h-4 w-4 mr-2" />
                { isInvitationLoading ? "Sending Invitation ......" : "Send Invites" }
              </Button>
            </CardFooter>
          </form>
        </Card>

        {/* Member List */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>
              Manage your team members and their roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-4"
            />

            <div className="space-y-4">
              {filteredMembers.map((member) => (
                <div
                  key={member.userId || member.invitationId}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="font-medium">{member.userEmail ?? member.invitationEmail}</div>
                      <div className="text-sm text-gray-500">
                        { member.role ? member.role : "Awaiting Response"}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {
                      member.status
                        &&
                      (
                        <span
                          className={`text-sm px-2 py-1 rounded-full ${
                            member.expiredAt && new Date(member.expiredAt) <= new Date()
                            ? "bg-red-100 text-red-700"
                            : member.status == "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                          }`}
                          >
                          {
                            member.expiredAt && new Date(member.expiredAt) <= new Date()
                              ? "Expired"
                              : member.status
                          }
                        </span>
                      )
                    }
                    <div className="flex items-center space-x-2">
                      {
                        member.role != "owner" && member.userId != user?.userId
                         ?
                        <Button
                          variant="outline"
                          className="border-red-300"
                          onClick={() => handleDeleteClick({
                            userId: member.userId,
                            userEmail: member.userEmail,
                            invitationId: member.invitationId,
                            invitationEmail: member.invitationEmail,
                          })}
                        >
                          <Trash className="text-red-500" />
                        </Button>
                          :
                        <Button
                          variant="outline"
                          className="border-gray-100"
                          disabled
                        >
                          <Trash className="text-gray-500" />
                        </Button>
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {
                memberToDelete?.userId
                  ? "Delete Team Member"
                  : "Cancel Invitation"
              }
            </AlertDialogTitle>
            <AlertDialogDescription>
              {
                memberToDelete?.userId
                  ? `Are you sure you want to remove ${memberToDelete?.userEmail} from the
                  team? This action cannot be undone.`
                  : `Are you sure you want to cancel invitation of ${memberToDelete?.invitationEmail} ? This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Team;
